import React, {
    useState,
    useRef,
    FormEvent,
    useEffect,
    useContext,
    useCallback,
} from 'react';
import {
    Student,
    UpdateStudentRequest,
    EnrollmentInput,
    CourseRef,
    InitialPayment,
} from '../types/student';
import { getCourses } from '../api/courses';
import { AuthContext } from '../context/AuthContext';
import Cropper, { Area } from 'react-easy-crop';

// Back Arrow Icon
const BackArrowIcon = () => (
    <svg width="20" height="20" className="block" viewBox="0 0 24 24" fill="none">
        <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const img = new window.Image();
        img.addEventListener('load', () => resolve(img));
        img.addEventListener('error', (err) => reject(err));
        img.setAttribute('crossOrigin', 'anonymous');
        img.src = url;
    });

async function getCroppedImg(imageSrc: string, crop: Area): Promise<string | null> {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    canvas.width = crop.width;
    canvas.height = crop.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(
        image,
        crop.x,
        crop.y,
        crop.width,
        crop.height,
        0,
        0,
        crop.width,
        crop.height
    );
    return canvas.toDataURL('image/jpeg');
}

function dataURLtoFile(dataurl: string, filename: string) {
    const arr = dataurl.split(','), mime = arr[0].match(/:(.*?);/)![1],
        bstr = atob(arr[1]), n = bstr.length, u8arr = new Uint8Array(n);
    for (let i = 0; i < n; i++) u8arr[i] = bstr.charCodeAt(i);
    return new File([u8arr], filename, { type: mime });
}

interface Props {
    student: Student;
    onSubmit: (
        id: string,
        data: UpdateStudentRequest | FormData
    ) => Promise<void>;
    onRemoveEnrollment: (
        id: string,
        enrollmentId: string
    ) => Promise<void>;
    onAddEnrollment: (
        id: string,
        enrollment: EnrollmentInput
    ) => Promise<void>;
    onCancel: () => void;
    initialStep?: 1 | 2 | 3 | 4;
}

export default function EditStudentForm({
                                            student,
                                            onSubmit,
                                            onRemoveEnrollment,
                                            onAddEnrollment,
                                            onCancel,
                                            initialStep = 1,
                                        }: Props) {
    const { user } = useContext(AuthContext);

    const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
    const formRef = useRef<HTMLFormElement>(null);
    const [error, setError] = useState<string>('');

    // Required + editable fields
    const [form, setForm] = useState<UpdateStudentRequest>({
        name: student.name,
        email: student.email,
        phone: student.phone,
        schoolName: student.schoolName ?? '',
        schoolCourse: student.schoolCourse ?? '',
        fathersName: student.fathersName ?? '',
        grandfathersName: student.grandfathersName ?? '',
        guardianNumber: student.guardianNumber ?? '',
        address: student.address ?? '',
        municipality: student.municipality ?? '',
        wardNo: student.wardNo ?? '',
        district: student.district ?? '',
        state: student.state ?? '',
        dateOfBirth: student.dateOfBirth ? student.dateOfBirth.slice(0, 10) : '',
    });

    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [showCropper, setShowCropper] = useState(false);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
    const [cropPreview, setCropPreview] = useState<string | null>(null);

    const [courses, setCourses] = useState<CourseRef[]>([]);
    const [enrollments, setEnrollments] = useState(student.enrollments);
    const [existingDiscounts, setExistingDiscounts] = useState<Record<string, number>>({});
    const [existingDiscountErrors, setExistingDiscountErrors] = useState<Record<string, string>>({});
    const [toAdd, setToAdd] = useState<EnrollmentInput[]>([]);
    const [newDiscountErrors, setNewDiscountErrors] = useState<Record<number, string>>({});
    const enrollOnly = initialStep === 2;

    // Load courses
    useEffect(() => {
        getCourses().then(setCourses).catch(console.error);
    }, []);

    // Reset form on student change
    useEffect(() => {
        setForm({
            name: student.name,
            email: student.email,
            phone: student.phone,
            schoolName: student.schoolName ?? '',
            schoolCourse: student.schoolCourse ?? '',
            fathersName: student.fathersName ?? '',
            grandfathersName: student.grandfathersName ?? '',
            guardianNumber: student.guardianNumber ?? '',
            address: student.address ?? '',
            municipality: student.municipality ?? '',
            wardNo: student.wardNo ?? '',
            district: student.district ?? '',
            state: student.state ?? '',
            dateOfBirth: student.dateOfBirth ? student.dateOfBirth.slice(0, 10) : '',
        });
        setImageFile(null);
        setImageUrl(null);
        setCropPreview(null);
        setShowCropper(false);
        setCroppedAreaPixels(null);
        setEnrollments(student.enrollments);
        setExistingDiscounts({});
        setExistingDiscountErrors({});
        setToAdd([]);
        setNewDiscountErrors({});
        setStep(initialStep);
        setError('');
    }, [student]);

    function handleNumberKeyPress(e: React.KeyboardEvent<HTMLInputElement>) {
        if (!/[0-9]/.test(e.key)) e.preventDefault();
    }

    // Allow editing only required fields directly
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name === 'phone' || name === 'guardianNumber' || name === 'wardNo') {
            if (!/^\d*$/.test(value)) return;
        }
        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    function validateEmail(email: string) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }
    function validatePhone(phone: string) {
        return /^(\d{7}|\d{10})$/.test(phone);
    }

    // Photo
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        setImageFile(file ?? null);
        setCropPreview(null);
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                setImageUrl(reader.result as string);
                setShowCropper(true);
            };
            reader.readAsDataURL(file);
        } else {
            setImageUrl(null);
        }
    };
    const handleRemovePhoto = () => {
        setImageFile(null);
        setImageUrl(null);
        setCropPreview(null);
        setCroppedAreaPixels(null);
    };
    const handleCropperDone = async () => {
        if (imageUrl && croppedAreaPixels) {
            const croppedImg = await getCroppedImg(imageUrl, croppedAreaPixels);
            setCropPreview(croppedImg);
        }
        setShowCropper(false);
    };
    const onCropComplete = useCallback(
        (croppedArea: Area, croppedAreaPixels: Area) => {
            setCroppedAreaPixels(croppedAreaPixels);
        },
        []
    );

    // Discount for existing enrollment
    const handleExistingDiscountChange = (
        enrollmentId: string,
        value: string
    ) => {
        const enrollment = enrollments.find(e => e.id === enrollmentId);
        const course: CourseRef | undefined = enrollment?.course;
        const maxDiscount = course?.maxViewerDiscount ?? course?.baseFee ?? 0;
        const disc = value === '' ? 0 : Number(value);
        let error = '';
        if (disc < 0) {
            error = 'Cannot be negative';
        }
        // Only viewers are capped
        else if (user?.role === 'viewer' && disc > maxDiscount) {
            error = `Max ${maxDiscount}`;
        }
        setExistingDiscountErrors((prev) => ({
            ...prev,
            [enrollmentId]: error,
        }));
        setExistingDiscounts((prev) => ({
            ...prev,
            [enrollmentId]: disc,
        }));
    };


    // New enrollment
    const addToAddEnrollment = () =>
        setToAdd((a) => [
            ...a,
            {
                course: '',
                discount: 0,
                enrollDate: new Date().toISOString().slice(0, 10),
                initialPayment: {
                    paymentDate: '',
                    receiptNumber: '',
                    amount: 0,
                },
            },
        ]);
    const handleNewEnrollmentChange = (
        idx: number,
        field: keyof Omit<EnrollmentInput, 'initialPayment'>,
        value: string
    ) => {
        setToAdd((a) => {
            const copy = [...a];
            if (field === 'discount') {
                const courseObj = courses.find(c => c.id === copy[idx].course);
                const maxDiscount = courseObj?.maxViewerDiscount ?? courseObj?.baseFee ?? 0;
                const disc = value === '' ? 0 : Number(value);
                let error = '';
                if (disc < 0) {
                    error = 'Cannot be negative';
                }
                // Only viewers are capped
                else if (user?.role === 'viewer' && disc > maxDiscount) {
                    error = `Max ${maxDiscount}`;
                }
                setNewDiscountErrors(prev => ({ ...prev, [idx]: error }));
                copy[idx].discount = disc;
            } else {
                // other fields…
                // @ts-ignore
                copy[idx][field] = value;
            }
            return copy;
        });
    };

    const handleNewEnrollmentPaymentChange = (
        idx: number,
        field: keyof InitialPayment,
        value: string | number
    ) => {
        setToAdd((a) => {
            const copy = [...a];
            if (!copy[idx].initialPayment) {
                copy[idx].initialPayment = {
                    paymentDate: '',
                    receiptNumber: '',
                    amount: 0,
                };
            }
            // @ts-ignore
            copy[idx].initialPayment[field] =
                field === 'amount' ? Number(value) : value;
            return copy;
        });
    };

    // Step navigation
    const handleNext = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setError('');
        if (step === 1) {
            if (!form.name?.trim()) {
                setError('Name is required to proceed.');
                return;
            }
            if (!form.email?.trim()) {
                setError('Email is required to proceed.');
                return;
            }
            if (!validateEmail(form.email)) {
                setError('Please enter a valid email address.');
                return;
            }
            if (!form.phone?.trim()) {
                setError('Phone number is required to proceed.');
                return;
            }
            if (!validatePhone(form.phone)) {
                setError('Phone must be either 7 or 10 digits.');
                return;
            }
            setStep(2);
        } else if (step === 2) {
            if (Object.values(existingDiscountErrors).some(e => e)) {
                setError('Fix discount errors');
                return;
            }
            if (Object.values(newDiscountErrors).some(e => e)) {
                setError('Fix new enrollment discount errors');
                return;
            }
            for (let i = 0; i < toAdd.length; i++) {
                const ne = toAdd[i];
                if (!ne.course) {
                    setError(`Please select a course for new enrollment #${i + 1}`);
                    return;
                }
            }
            setStep(3);
        } else if (step === 3) {
            setStep(4);
        }
    };
    const handleBack = () => {
        setError('');
        if (step === 4) setStep(3);
        else if (step === 3) setStep(2);
        else setStep(1);
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setError('');
        if (!form.name?.trim()) {
            setError('Name is required to proceed.');
            setStep(1);
            return;
        }
        if (!form.email?.trim()) {
            setError('Email is required to proceed.');
            setStep(1);
            return;
        }
        if (!validateEmail(form.email)) {
            setError('Please enter a valid email address.');
            setStep(1);
            return;
        }
        if (!form.phone?.trim()) {
            setError('Phone number is required to proceed.');
            setStep(1);
            return;
        }
        if (!validatePhone(form.phone)) {
            setError('Phone must be 10 digits.');
            setStep(1);
            return;
        }
        if (Object.values(existingDiscountErrors).some(Boolean)) {
            setError('Fix discount errors');
            setStep(2);
            return;
        }
        if (Object.values(newDiscountErrors).some(Boolean)) {
            setError('Fix new enrollment discount errors');
            setStep(2);
            return;
        }
        for (let i = 0; i < toAdd.length; i++) {
            const ne = toAdd[i];
            if (!ne.course) {
                setError(`Please select a course for new enrollment #${i + 1}`);
                setStep(2);
                return;
            }
        }
        try {
            // Add new enrollments (with optional fields)
            for (let ne of toAdd) {
                await onAddEnrollment(student.id, ne);
            }
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => {
                if (v !== undefined && v !== null) fd.append(k, String(v));
            });
            if (cropPreview) {
                const file = dataURLtoFile(cropPreview, imageFile?.name || "photo.jpg");
                fd.append('image', file);
            } else if (imageFile) {
                fd.append('image', imageFile);
            }
            if (croppedAreaPixels) {
                fd.append('imageCrop', JSON.stringify(croppedAreaPixels));
            }
            // Only send updates for discounts that have changed
            const updates = Object.entries(existingDiscounts)
                .filter(([eid, disc]) => {
                    const orig = student.enrollments.find((e) => e.id === eid);
                    return orig && orig.discount !== disc;
                })
                .map(([enrollmentId, discount]) => ({ enrollmentId, discount }));

            if (updates.length) {
                fd.append('updateEnrollments', JSON.stringify(updates));
            }
            await onSubmit(student.id, fd);
            onCancel();
        } catch (err: any) {
            setError(err.response?.data?.message || err.message);
        }
    };

    return (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-slate-950/40 p-4 backdrop-blur-[2px]">
            <div className="relative flex max-h-[92vh] w-full max-w-[920px] flex-col overflow-hidden rounded-[18px] border border-slate-200 bg-white shadow-[0_24px_80px_rgba(15,23,42,0.20)]">
                <div className="flex shrink-0 items-center justify-between gap-4 border-b border-slate-100 px-6 py-5 max-sm:px-4">
                    {step > 1 ? (
                        <button
                            type="button"
                            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px] border border-slate-200 bg-white p-0 text-slate-500 transition hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200"
                            onClick={handleBack}
                            aria-label="Back"
                        >
                            <BackArrowIcon />
                        </button>
                    ) : (
                        <div className="h-9 w-9 shrink-0" />
                    )}
                    <div className="min-w-0 flex-1 text-center">
                        <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.12em] text-blue-600">{enrollOnly ? 'Enrollment management' : `Student management · Step ${step} of 4`}</div>
                        <h1 className="m-0 text-[26px] font-bold tracking-[-0.03em] text-slate-950 max-sm:text-[22px]">Edit {student.name}</h1>
                        <p className="mb-0 mt-1 text-[13px] leading-5 text-slate-500">
                            Update student & enrollment details
                        </p>
                    </div>
                    <button
                        type="button"
                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px] border border-slate-200 bg-white p-0 text-[16px] text-slate-500 transition hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200"
                        onClick={onCancel}
                        aria-label="Close"
                    >
                        ✕
                    </button>
                </div>
                {error && <div className="mx-6 mt-4 rounded-[9px] border border-red-200 bg-red-50 px-3 py-2 text-[12px] font-medium text-red-700 max-sm:mx-4">{error}</div>}
                {showCropper && imageUrl && (
                    <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
                        <div className="w-full max-w-[440px] rounded-[16px] border border-slate-200 bg-white p-5 shadow-[0_24px_80px_rgba(15,23,42,0.28)]">
                            <h3 className="mb-4 mt-0 text-[16px] font-bold text-slate-950">Crop your photo</h3>
                            <div className="relative aspect-square w-full overflow-hidden rounded-[12px] bg-slate-900">
                                <Cropper
                                    image={imageUrl}
                                    crop={crop}
                                    zoom={zoom}
                                    aspect={1}
                                    onCropChange={setCrop}
                                    onZoomChange={setZoom}
                                    onCropComplete={onCropComplete}
                                />
                            </div>
                            <div className="mt-4 flex items-center justify-end gap-2">
                                <button
                                    type="button"
                                    className="inline-flex h-10 items-center justify-center rounded-[9px] border border-slate-200 bg-white px-4 text-[13px] font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-950 focus:outline-none focus:ring-2 focus:ring-slate-200"
                                    onClick={() => {
                                        setShowCropper(false);
                                        setImageFile(null);
                                        setImageUrl(null);
                                        setCropPreview(null);
                                        setCroppedAreaPixels(null);
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="inline-flex h-10 items-center justify-center rounded-[9px] border border-transparent bg-[#0d162a] px-5 text-[13px] font-semibold text-white transition hover:bg-[#172239] focus:outline-none focus:ring-2 focus:ring-slate-300"
                                    onClick={handleCropperDone}
                                >
                                    Done
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                <form
                    ref={formRef}
                    className="grid min-h-0 min-w-0 grid-cols-1 gap-y-4 gap-x-6 overflow-y-auto overflow-x-hidden px-6 py-5 md:grid-cols-2 max-sm:px-4"
                    onSubmit={handleSubmit}
                >
                    {/* Step 1: Required fields */}
                    {!enrollOnly && step === 1 && (
                        <>
                            <div className="min-w-0 px-1">
                                <label className="mb-1.5 block text-[12px] font-semibold text-slate-700" htmlFor="name">
                                    Name *
                                </label>
                                <input
                                    id="name"
                                    name="name"
                                    type="text"
                                    className="box-border h-11 w-full min-w-0 max-w-full rounded-[9px] !border !border-solid !border-slate-200 bg-white px-3.5 text-[14px] font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:!border-slate-300 focus:!border-slate-400 focus:ring-2 focus:ring-slate-100"
                                    value={form.name}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className="min-w-0 px-1">
                                <label className="mb-1.5 block text-[12px] font-semibold text-slate-700" htmlFor="email">
                                    Email *
                                </label>
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    className="box-border h-11 w-full min-w-0 max-w-full rounded-[9px] !border !border-solid !border-slate-200 bg-white px-3.5 text-[14px] font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:!border-slate-300 focus:!border-slate-400 focus:ring-2 focus:ring-slate-100"
                                    value={form.email}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className="min-w-0 px-1">
                                <label className="mb-1.5 block text-[12px] font-semibold text-slate-700" htmlFor="phone">
                                    Phone *
                                </label>
                                <input
                                    id="phone"
                                    name="phone"
                                    type="text"
                                    className="box-border h-11 w-full min-w-0 max-w-full rounded-[9px] !border !border-solid !border-slate-200 bg-white px-3.5 text-[14px] font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:!border-slate-300 focus:!border-slate-400 focus:ring-2 focus:ring-slate-100"
                                    value={form.phone}
                                    onChange={handleChange}
                                    onKeyPress={handleNumberKeyPress}
                                    inputMode="numeric"
                                    pattern="\d{7}|\d{10}"
                                    maxLength={10}
                                    required
                                />
                            </div>
                        </>
                    )}
                    {/* Step 2: Enrollments */}
                    {step === 2 && (
                        <>
                            {enrollments.map((en, idx) => {
                                const co = en.course;
                                const maxDiscount = co.maxViewerDiscount ?? co.baseFee;
                                const discountValue = existingDiscounts[en.id] ?? en.discount ?? 0;
                                const err = existingDiscountErrors[en.id] || '';
                                return (
                                    <div key={en.id} className="col-span-full min-w-0 rounded-[14px] border border-slate-200 bg-slate-50/60 p-4 md:p-5">
                                        <h3 className="mb-4 mt-0 text-[15px] font-bold text-slate-950">
                                            Enrollment #{idx + 1}
                                        </h3>
                                        <div className="mb-3 text-[13px] text-slate-600">
                                            <strong>{co.name}</strong> ({co.periodMonths} mo)
                                        </div>
                                        <div className="min-w-0 px-1">
                                            <label className="mb-1.5 block text-[12px] font-semibold text-slate-700">
                                                Discount (Max {maxDiscount})
                                            </label>
                                            <input
                                            type="text"
                                            className="box-border h-11 w-full min-w-0 max-w-full rounded-[9px] !border !border-solid !border-slate-200 bg-white px-3.5 text-[14px] font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:!border-slate-300 focus:!border-slate-400 focus:ring-2 focus:ring-slate-100"
                                            value={discountValue}
                                            onChange={e =>
                                            handleExistingDiscountChange(en.id, e.target.value)
                                            }
                                            onKeyPress={handleNumberKeyPress}
                                            inputMode="numeric"
                                            />

                                            {err && (
                                                <div className="mx-6 mt-4 rounded-[9px] border border-red-200 bg-red-50 px-3 py-2 text-[12px] font-medium text-red-700 max-sm:mx-4">
                                                    {err}
                                                </div>
                                            )}
                                        </div>
                                        <div className="mt-3 grid grid-cols-1 gap-1.5 rounded-[10px] border border-slate-200 bg-white px-3.5 py-3 text-[12px] text-slate-600 sm:grid-cols-3 [&_p]:m-0">
                                            <p>
                                                <strong>Base fee:</strong> {co.baseFee}
                                            </p>
                                            <p>
                                                <strong>Net fee:</strong> {Math.max(co.baseFee - discountValue, 0)}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                            {toAdd.map((ne, i) => {
                                const available = courses.filter(
                                    (c) =>
                                        !enrollments.some((e) => e.course.id === c.id) &&
                                        !toAdd.some((_x, xi) => xi !== i && _x.course === c.id)
                                );
                                const co = courses.find((c) => c.id === ne.course);
                                const maxDiscount = co?.maxViewerDiscount ?? co?.baseFee ?? 0;
                                const err = newDiscountErrors[i] || '';
                                return (
                                    <div key={i} className="col-span-full min-w-0 rounded-[14px] border border-slate-200 bg-slate-50/60 p-4 md:p-5">
                                        <h3 className="mb-4 mt-0 text-[15px] font-bold text-slate-950">
                                            New Enrollment #{enrollments.length + i + 1}
                                        </h3>
                                        <div className="min-w-0 px-1">
                                            <label className="mb-1.5 block text-[12px] font-semibold text-slate-700">Course *</label>
                                            <select
                                                className="box-border h-11 w-full min-w-0 max-w-full rounded-[9px] !border !border-solid !border-slate-200 bg-white px-3.5 text-[14px] font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:!border-slate-300 focus:!border-slate-400 focus:ring-2 focus:ring-slate-100"
                                                value={ne.course}
                                                onChange={(e) =>
                                                    handleNewEnrollmentChange(
                                                        i,
                                                        'course',
                                                        e.target.value
                                                    )
                                                }
                                                required
                                            >
                                                <option value="">Select…</option>
                                                {available.map((c) => (
                                                    <option key={c.id} value={c.id}>
                                                        {c.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        {co && (
                                            <div className="mt-3 grid grid-cols-1 gap-1.5 rounded-[10px] border border-slate-200 bg-white px-3.5 py-3 text-[12px] text-slate-600 sm:grid-cols-3 [&_p]:m-0">
                                                <p>
                                                    <strong>Base fee:</strong> {co.baseFee}
                                                </p>
                                                <p>
                                                    <strong>Net fee:</strong> {Math.max(co.baseFee - (ne.discount ?? 0), 0)}
                                                </p>
                                                <p>
                                                    <strong>Max Discount:</strong> {maxDiscount}
                                                </p>
                                            </div>
                                        )}
                                        <div className="mt-4 grid min-w-0 grid-cols-1 gap-y-4 gap-x-5 md:grid-cols-2">
                                            <div className="min-w-0 px-1">
                                                <label className="mb-1.5 block text-[12px] font-semibold text-slate-700">
                                                    Discount
                                                </label>
                                                <input
                                                    type="text"
                                                    className="box-border h-11 w-full min-w-0 max-w-full rounded-[9px] !border !border-solid !border-slate-200 bg-white px-3.5 text-[14px] font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:!border-slate-300 focus:!border-slate-400 focus:ring-2 focus:ring-slate-100"
                                                    value={ne.discount ?? ''}
                                                    onChange={(e) =>
                                                        handleNewEnrollmentChange(
                                                            i,
                                                            'discount',
                                                            e.target.value
                                                        )
                                                    }
                                                    onKeyPress={handleNumberKeyPress}
                                                    inputMode="numeric"
                                                />
                                                {err && (
                                                    <div className="mx-6 mt-4 rounded-[9px] border border-red-200 bg-red-50 px-3 py-2 text-[12px] font-medium text-red-700 max-sm:mx-4">
                                                        {err}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="min-w-0 px-1">
                                                <label className="mb-1.5 block text-[12px] font-semibold text-slate-700">
                                                    Enroll Date
                                                </label>
                                                <input
                                                    type="date"
                                                    className="box-border h-11 w-full min-w-0 max-w-full rounded-[9px] !border !border-solid !border-slate-200 bg-white px-3.5 text-[14px] font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:!border-slate-300 focus:!border-slate-400 focus:ring-2 focus:ring-slate-100"
                                                    value={ne.enrollDate ?? ''}
                                                    onChange={(e) =>
                                                        handleNewEnrollmentChange(
                                                            i,
                                                            'enrollDate',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </div>
                                        </div>
                                        <div className="mt-3 grid min-w-0 grid-cols-1 gap-y-4 gap-x-5 md:grid-cols-3">
                                            <div className="min-w-0 px-1">
                                                <label className="mb-1.5 block text-[12px] font-semibold text-slate-700">
                                                    Payment Date
                                                </label>
                                                <input
                                                    type="date"
                                                    className="box-border h-11 w-full min-w-0 max-w-full rounded-[9px] !border !border-solid !border-slate-200 bg-white px-3.5 text-[14px] font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:!border-slate-300 focus:!border-slate-400 focus:ring-2 focus:ring-slate-100"
                                                    value={ne.initialPayment?.paymentDate ?? ''}
                                                    onChange={(e) =>
                                                        handleNewEnrollmentPaymentChange(
                                                            i,
                                                            'paymentDate',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </div>
                                            <div className="min-w-0 px-1">
                                                <label className="mb-1.5 block text-[12px] font-semibold text-slate-700">
                                                    Receipt #
                                                </label>
                                                <input
                                                    type="text"
                                                    className="box-border h-11 w-full min-w-0 max-w-full rounded-[9px] !border !border-solid !border-slate-200 bg-white px-3.5 text-[14px] font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:!border-slate-300 focus:!border-slate-400 focus:ring-2 focus:ring-slate-100"
                                                    value={ne.initialPayment?.receiptNumber ?? ''}
                                                    onChange={(e) =>
                                                        handleNewEnrollmentPaymentChange(
                                                            i,
                                                            'receiptNumber',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </div>
                                            <div className="min-w-0 px-1">
                                                <label className="mb-1.5 block text-[12px] font-semibold text-slate-700">
                                                    Amount
                                                </label>
                                                <input
                                                    type="text"
                                                    className="box-border h-11 w-full min-w-0 max-w-full rounded-[9px] !border !border-solid !border-slate-200 bg-white px-3.5 text-[14px] font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:!border-slate-300 focus:!border-slate-400 focus:ring-2 focus:ring-slate-100"
                                                    value={
                                                        ne.initialPayment?.amount === 0
                                                            ? ''
                                                            : ne.initialPayment?.amount ?? ''
                                                    }
                                                    onChange={(e) =>
                                                        handleNewEnrollmentPaymentChange(
                                                            i,
                                                            'amount',
                                                            e.target.value
                                                        )
                                                    }
                                                    onKeyPress={handleNumberKeyPress}
                                                    inputMode="numeric"
                                                />
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className="inline-flex h-10 items-center justify-center rounded-[9px] border border-red-200 bg-white px-4 text-[13px] font-semibold text-red-600 transition hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-100"
                                            onClick={() =>
                                                setToAdd((a) => a.filter((_, xi) => xi !== i))
                                            }
                                        >
                                            Remove this course
                                        </button>
                                    </div>
                                );
                            })}
                            <button
                                type="button"
                                className="inline-flex h-10 items-center justify-center rounded-[9px] border border-slate-200 bg-white px-4 text-[13px] font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-950 focus:outline-none focus:ring-2 focus:ring-slate-200"
                               
                                onClick={addToAddEnrollment}
                            >
                                + Add another course
                            </button>
                        </>
                    )}
                    {/* Step 3: Optional Details */}
                    {!enrollOnly && step === 3 && (
                        <>
                            <h3 className="col-span-full mb-0 mt-2 text-[14px] font-bold text-slate-950">Academic Details (optional)</h3>
                            <div className="min-w-0 px-1">
                                <label className="mb-1.5 block text-[12px] font-semibold text-slate-700" htmlFor="schoolName">
                                    School Name
                                </label>
                                <input
                                    id="schoolName"
                                    name="schoolName"
                                    type="text"
                                    className="box-border h-11 w-full min-w-0 max-w-full rounded-[9px] !border !border-solid !border-slate-200 bg-white px-3.5 text-[14px] font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:!border-slate-300 focus:!border-slate-400 focus:ring-2 focus:ring-slate-100"
                                    value={form.schoolName}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="min-w-0 px-1">
                                <label className="mb-1.5 block text-[12px] font-semibold text-slate-700" htmlFor="schoolCourse">
                                    School Course
                                </label>
                                <input
                                    id="schoolCourse"
                                    name="schoolCourse"
                                    type="text"
                                    className="box-border h-11 w-full min-w-0 max-w-full rounded-[9px] !border !border-solid !border-slate-200 bg-white px-3.5 text-[14px] font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:!border-slate-300 focus:!border-slate-400 focus:ring-2 focus:ring-slate-100"
                                    value={form.schoolCourse}
                                    onChange={handleChange}
                                />
                            </div>
                            <h3 className="col-span-full mb-0 mt-2 text-[14px] font-bold text-slate-950">Parent/Guardian (optional)</h3>
                            {[
                                { name: 'fathersName', label: "Father's Name" },
                                { name: 'grandfathersName', label: "Grandfather's Name" },
                                { name: 'guardianNumber', label: 'Guardian Number', type: 'tel' },
                            ].map((f) => (
                                <div key={f.name} className="min-w-0 px-1">
                                    <label className="mb-1.5 block text-[12px] font-semibold text-slate-700" htmlFor={f.name}>
                                        {f.label}
                                    </label>
                                    <input
                                        id={f.name}
                                        name={f.name}
                                        type={f.type || 'text'}
                                        className="box-border h-11 w-full min-w-0 max-w-full rounded-[9px] !border !border-solid !border-slate-200 bg-white px-3.5 text-[14px] font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:!border-slate-300 focus:!border-slate-400 focus:ring-2 focus:ring-slate-100"
                                        value={form[f.name as keyof typeof form]}
                                        onChange={handleChange}
                                        onKeyPress={
                                            f.name === 'guardianNumber'
                                                ? handleNumberKeyPress
                                                : undefined
                                        }
                                        inputMode={
                                            f.name === 'guardianNumber'
                                                ? 'numeric'
                                                : undefined
                                        }
                                    />
                                </div>
                            ))}
                            <h3 className="col-span-full mb-0 mt-2 text-[14px] font-bold text-slate-950">Other Details (optional)</h3>
                            <div className="col-span-full grid min-w-0 grid-cols-1 gap-y-4 gap-x-6 md:grid-cols-2">
                                <div className="min-w-0 px-1">
                                    <label className="mb-1.5 block text-[12px] font-semibold text-slate-700" htmlFor="dateOfBirth">
                                        Date of Birth
                                    </label>
                                    <input
                                        id="dateOfBirth"
                                        name="dateOfBirth"
                                        type="date"
                                        className="box-border h-11 w-full min-w-0 max-w-full rounded-[9px] !border !border-solid !border-slate-200 bg-white px-3.5 text-[14px] font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:!border-slate-300 focus:!border-slate-400 focus:ring-2 focus:ring-slate-100"
                                        value={form.dateOfBirth}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div className="min-w-0 px-1">
                                    <label className="mb-1.5 block text-[12px] font-semibold text-slate-700" htmlFor="referredBy">
                                        Referred By
                                    </label>
                                    <input
                                        id="referredBy"
                                        name="referredBy"
                                        type="text"
                                        className="box-border h-11 w-full min-w-0 max-w-full rounded-[9px] !border !border-solid !border-slate-200 bg-white px-3.5 text-[14px] font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:!border-slate-300 focus:!border-slate-400 focus:ring-2 focus:ring-slate-100"
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                            <div className="min-w-0 px-1">
                                <label className="mb-1.5 block text-[12px] font-semibold text-slate-700" htmlFor="photo">
                                    Photo
                                </label>
                                <input
                                    id="photo"
                                    type="file"
                                    accept="image/*"
                                    className="box-border h-11 w-full min-w-0 max-w-full rounded-[9px] !border !border-solid !border-slate-200 bg-white px-3.5 text-[14px] font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:!border-slate-300 focus:!border-slate-400 focus:ring-2 focus:ring-slate-100"
                                    onChange={handleFileChange}
                                />
                                {cropPreview && (
                                    <div className="mt-3 flex flex-wrap items-center gap-2">
                                        <img
                                            src={cropPreview}
                                            alt="Crop Preview"
                                            className="h-[120px] w-[120px] rounded-[10px] border border-slate-200 object-cover"
                                        />
                                        <button
                                            type="button"
                                            className="inline-flex h-10 items-center justify-center rounded-[9px] border border-slate-200 bg-white px-4 text-[13px] font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-950 focus:outline-none focus:ring-2 focus:ring-slate-200"
                                           
                                            onClick={() => setShowCropper(true)}
                                        >
                                            Re-crop
                                        </button>
                                        <button
                                            type="button"
                                            className="inline-flex h-10 items-center justify-center rounded-[9px] border border-red-200 bg-white px-4 text-[13px] font-semibold text-red-600 transition hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-100"
                                           
                                            onClick={handleRemovePhoto}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                )}
                                {!cropPreview && imageFile && (
                                    <div className="mt-3 flex flex-wrap items-center gap-2">
                                        <span>Photo selected</span>
                                        <button
                                            type="button"
                                            className="inline-flex h-10 items-center justify-center rounded-[9px] border border-slate-200 bg-white px-4 text-[13px] font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-950 focus:outline-none focus:ring-2 focus:ring-slate-200"
                                           
                                            onClick={() => setShowCropper(true)}
                                        >
                                            Crop
                                        </button>
                                        <button
                                            type="button"
                                            className="inline-flex h-10 items-center justify-center rounded-[9px] border border-red-200 bg-white px-4 text-[13px] font-semibold text-red-600 transition hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-100"
                                           
                                            onClick={handleRemovePhoto}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                    {/* Step 4: Address */}
                    {!enrollOnly && step === 4 && (
                        <>
                            <h3 className="col-span-full mb-0 mt-2 text-[14px] font-bold text-slate-950">Address Details (optional)</h3>
                            {[
                                { name: 'address', label: 'Address' },
                                { name: 'municipality', label: 'Municipality' },
                                { name: 'wardNo', label: 'Ward No.', type: 'text' },
                                { name: 'district', label: 'District' },
                                { name: 'state', label: 'State' },
                            ].map((f) => (
                                <div key={f.name} className="min-w-0 px-1">
                                    <label className="mb-1.5 block text-[12px] font-semibold text-slate-700" htmlFor={f.name}>
                                        {f.label}
                                    </label>
                                    <input
                                        id={f.name}
                                        name={f.name}
                                        type={f.type || 'text'}
                                        className="box-border h-11 w-full min-w-0 max-w-full rounded-[9px] !border !border-solid !border-slate-200 bg-white px-3.5 text-[14px] font-medium text-slate-800 outline-none transition placeholder:text-slate-400 hover:!border-slate-300 focus:!border-slate-400 focus:ring-2 focus:ring-slate-100"
                                        value={form[f.name as keyof typeof form]}
                                        onChange={handleChange}
                                        onKeyPress={
                                            f.name === 'wardNo'
                                                ? handleNumberKeyPress
                                                : undefined
                                        }
                                        inputMode={
                                            f.name === 'wardNo'
                                                ? 'numeric'
                                                : undefined
                                        }
                                    />
                                </div>
                            ))}
                        </>
                    )}
                    {/* Footer Buttons */}
                    <div className="col-span-full mt-1 flex items-center justify-end gap-2 border-t border-slate-100 pt-4 max-sm:flex-col-reverse max-sm:items-stretch">
                        {enrollOnly ? (
                            /* Enroll-only mode: show Cancel + Enroll */
                            <>
                                <button
                                    type="button"
                                    className="inline-flex h-10 items-center justify-center rounded-[9px] border border-slate-200 bg-white px-4 text-[13px] font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-950 focus:outline-none focus:ring-2 focus:ring-slate-200"
                                    onClick={onCancel}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="inline-flex h-10 items-center justify-center rounded-[9px] border border-transparent bg-[#0d162a] px-5 text-[13px] font-semibold text-white transition hover:bg-[#172239] focus:outline-none focus:ring-2 focus:ring-slate-300"
                                   
                                >
                                    Enroll
                                </button>
                            </>
                        ) : (
                            /* Normal multi-step footer */
                            step === 1 ? (
                                <>
                                    <button
                                        type="button"
                                        className="inline-flex h-10 items-center justify-center rounded-[9px] border border-slate-200 bg-white px-4 text-[13px] font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-950 focus:outline-none focus:ring-2 focus:ring-slate-200"
                                        onClick={onCancel}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="inline-flex h-10 items-center justify-center rounded-[9px] border border-transparent bg-[#0d162a] px-5 text-[13px] font-semibold text-white transition hover:bg-[#172239] focus:outline-none focus:ring-2 focus:ring-slate-300"
                                        onClick={handleNext}
                                       
                                    >
                                        Next
                                    </button>
                                </>
                            ) : step === 2 || step === 3 ? (
                                <>
                                    <button
                                        type="button"
                                        className="inline-flex h-10 items-center justify-center rounded-[9px] border border-slate-200 bg-white px-4 text-[13px] font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-950 focus:outline-none focus:ring-2 focus:ring-slate-200"
                                        onClick={handleBack}
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="button"
                                        className="inline-flex h-10 items-center justify-center rounded-[9px] border border-transparent bg-[#0d162a] px-5 text-[13px] font-semibold text-white transition hover:bg-[#172239] focus:outline-none focus:ring-2 focus:ring-slate-300"
                                        onClick={handleNext}
                                    >
                                        Next
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        className="inline-flex h-10 items-center justify-center rounded-[9px] border border-slate-200 bg-white px-4 text-[13px] font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-950 focus:outline-none focus:ring-2 focus:ring-slate-200"
                                        onClick={handleBack}
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="submit"
                                        className="inline-flex h-10 items-center justify-center rounded-[9px] border border-transparent bg-[#0d162a] px-5 text-[13px] font-semibold text-white transition hover:bg-[#172239] focus:outline-none focus:ring-2 focus:ring-slate-300"
                                    >
                                        Save
                                    </button>
                                </>
                            )
                        )}
                    </div>

                </form>
            </div>
        </div>
    );
}
